(async () => {
  const { Fancybox } = await import("@fancyapps/ui");
  const { default: Cookies } = await import("js-cookie");

  if (!customElements.get("country-popup")) {
    class CountryPopup extends HTMLElement {
      constructor() {
        super();

        // Scope elements
        this.countryPopupWrapper = this.querySelector(
          "[data-country-popup-wrapper]"
        );
        if (!this.countryPopupWrapper) return;

        this.buttonStay = this.countryPopupWrapper.querySelector(
          "[data-country-popup-stay]"
        );
        this.countryName = this.countryPopupWrapper.querySelector(
          "[data-country-popup-name]"
        );
        this.countryUrl = this.countryPopupWrapper.querySelector(
          "[data-country-popup-url]"
        );

        if (!this.buttonStay || !this.countryName || !this.countryUrl) return;

        // Cookie settings
        this.countryPopupCookieIdentifier = "country_popup";
        this.dataOptions = {};

        // Get options from element data
        if (this?.dataset.options) {
          try {
            this.dataOptions = JSON.parse(this.dataset.options);
          } catch (e) {
            return;
          }
        }

        // Bind event handlers
        this.buttonStay.addEventListener(
          "click",
          this.closeCountryPopup.bind(this)
        );

        this.init();

        // Listen to extra events when in Shopify editor
        if (Shopify.designMode) {
          window.addEventListener("shopify:section:load", (event) => {
            if (event.detail.sectionId === "country-popup") {
              Fancybox.close();
              this.init();
            }
          });
        }
      }

      /**
       * Init the country popup plugin
       * @returns void
       */
      init() {
        try {
          // If country popup days is set to zero, remove cookie if exists
          if (parseInt(this.dataOptions.expiresInDays) === 0) {
            Cookies.remove(this.countryPopupCookieIdentifier);
          }

          // Get cookie - if it doesn't exist or is 'true', we should show the popup
          this.countryPopupCookie = Cookies.get(
            this.countryPopupCookieIdentifier
          );
          const shouldShowPopup =
            !this.countryPopupCookie || this.countryPopupCookie === "true";

          // Return early if we shouldn't show the popup
          if (!shouldShowPopup) return false;

          // Check country suggestions
          this.checkCountrySuggestions();
        } catch (error) {
          console.error(error);
        }
      }

      /**
       * Check country suggestions using Shopify's API
       */
      async checkCountrySuggestions() {
        try {
          let data;

          const response = await fetch(
            window.Shopify.routes.root +
              "browsing_context_suggestions.json" +
              "?country[enabled]=true" +
              `&country[exclude]=${this.dataOptions.currentCountryCode}` +
              "&language[enabled]=true" +
              `&language[exclude]=${this.dataOptions.currentLanguage}`
          );

          data = await response.json();

          if (!data.detected_values?.country || !data.suggestions?.[0]) return;

          const { country } = data.detected_values;
          const suggestion = data.suggestions[0];

          if (
            suggestion.confidence < 0.8 ||
            country.handle === this.dataOptions.currentCountryCode
          )
            return;

          if (country.market?.handle === this.dataOptions.currentMarket) return;

          const localizedUrl = this.getLocalizedUrl(
            country.handle,
            suggestion.parts.language?.handle
          );

          // Update popup content to add The infront of Netherlands
          if (country.name == "Netherlands")
            country.name = "The " + country.name;

          this.countryName.textContent = country.name;
          this.countryUrl.href = localizedUrl;
          this.renderCountryPopup();
        } catch (error) {
          console.error(error);
        }
      }

      /**
       * Get localized URL for the suggested country/language
       * @param {string} countryCode
       * @param {string} languageCode
       * @returns {string}
       */
      getLocalizedUrl(countryCode, languageCode) {
        const url = new URL(window.location.href);
        const newUrl = new URL(url.origin);

        // Add path and search params
        newUrl.pathname = url.pathname;
        newUrl.search = url.search;

        // Add country code
        if (countryCode) {
          newUrl.searchParams.set("country", countryCode);
        }

        // Add language code if available
        if (languageCode) {
          newUrl.searchParams.set("language", languageCode);
        }

        return newUrl.toString();
      }

      /**
       * Render the country popup
       * @returns void
       */
      renderCountryPopup() {
        // Set cookie to true when showing popup
        Cookies.set(this.countryPopupCookieIdentifier, "true", {
          expires: parseInt(this.dataOptions.expiresInDays),
        });

        // Open fancybox and set callback for destroy event
        Fancybox.show(
          [
            {
              src: this.countryPopupWrapper,
              type: "html",
            },
          ],
          {
            mainClass: "fancybox--is-country-popup",
            on: {
              destroy: () => {
                this.closeCountryPopup();
              },
            },
          }
        );
      }

      /**
       * Close country popup
       * @returns void
       */
      closeCountryPopup() {
        // Set cookie to false when user chooses to stay
        Cookies.set(this.countryPopupCookieIdentifier, "false", {
          expires: parseInt(this.dataOptions.expiresInDays),
        });

        Fancybox.close();
      }
    }

    window.CountryPopup = CountryPopup;
    customElements.define("country-popup", CountryPopup);
  }
})();
